import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("neuromap_cookie_consent");
    if (!consent) {
      // Show banner after short delay
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = async (analyticsVal, marketingVal) => {
    const consent = {
      essential: true,
      analytics: analyticsVal,
      marketing: marketingVal,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("neuromap_cookie_consent", JSON.stringify(consent));
    setVisible(false);

    // Log to backend for admin analytics
    try {
      await axios.post(`${API}/cookie-consent`, {
        analytics: analyticsVal,
        marketing: marketingVal,
      });
    } catch {
      // Non-blocking
    }

    // Reload to activate/deactivate pixels
    if (marketingVal) {
      window.location.reload();
    }
  };

  const acceptAll = () => saveConsent(true, true);
  const acceptEssential = () => saveConsent(false, false);
  const savePreferences = () => saveConsent(analytics, marketing);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4" data-testid="cookie-consent-banner">
      <div className="max-w-2xl mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="p-5">
          <div className="flex items-start gap-3 mb-3">
            <Shield className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-slate-800 text-base" style={{ fontFamily: "Nunito" }}>Cookie Preferences</h3>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                We use cookies to make this app work, understand how it's used, and show relevant content.
                Read our{" "}
                <Link to="/privacy" className="text-indigo-600 hover:text-indigo-700 font-semibold underline" data-testid="privacy-link">
                  Privacy Policy
                </Link>{" "}
                for details.
              </p>
            </div>
          </div>

          {/* Cookie categories */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1 text-sm text-indigo-600 font-semibold mb-3 hover:text-indigo-700"
            data-testid="cookie-details-toggle"
          >
            Manage preferences
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showDetails && (
            <div className="space-y-3 mb-4 animate-fade-in" data-testid="cookie-details">
              {/* Essential - always on */}
              <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-700">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Essential Cookies</p>
                  <p className="text-xs text-slate-500">Required for the app to work. Cannot be disabled.</p>
                </div>
                <div className="w-10 h-6 bg-emerald-500 rounded-full flex items-center justify-end px-1">
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-700">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Analytics Cookies</p>
                  <p className="text-xs text-slate-500">Help us understand how the app is used.</p>
                </div>
                <button
                  onClick={() => setAnalytics(!analytics)}
                  className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${analytics ? "bg-emerald-500 justify-end" : "bg-slate-300 justify-start"}`}
                  data-testid="cookie-analytics-toggle"
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-700">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Marketing Cookies</p>
                  <p className="text-xs text-slate-500">Used by Meta and TikTok for personalised advertising.</p>
                </div>
                <button
                  onClick={() => setMarketing(!marketing)}
                  className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${marketing ? "bg-emerald-500 justify-end" : "bg-slate-300 justify-start"}`}
                  data-testid="cookie-marketing-toggle"
                >
                  <div className="w-4 h-4 bg-white rounded-full" />
                </button>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={acceptAll}
              className="btn-primary text-sm py-2 px-5"
              data-testid="cookie-accept-all"
            >
              Accept All
            </button>
            {showDetails ? (
              <button
                onClick={savePreferences}
                className="btn-secondary text-sm py-2 px-5"
                data-testid="cookie-save-preferences"
              >
                Save Preferences
              </button>
            ) : (
              <button
                onClick={acceptEssential}
                className="btn-secondary text-sm py-2 px-5"
                data-testid="cookie-essential-only"
              >
                Essential Only
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Check if user has consented to a specific cookie type.
 */
export const hasConsent = (type) => {
  const consent = localStorage.getItem("neuromap_cookie_consent");
  if (!consent) return false;
  try {
    return JSON.parse(consent)[type] === true;
  } catch {
    return false;
  }
};

export default CookieConsent;
